<template>
  <main class="photo-page">
    <aside class="photo-sidebar">
      <a class="photo-brand" href="/">
        <span>▧</span>
        <strong>My Photos</strong>
      </a>

      <nav class="photo-nav" aria-label="照片导航">
        <button class="active" type="button">▧ 照片库</button>
        <button type="button" @click="clearFilters">▣ 全部照片</button>
        <button type="button" @click="showFavorites">☆ 收藏夹</button>
        <a href="/">⌂ 返回首页</a>
      </nav>

      <section class="photo-side-section">
        <h2>标签</h2>
        <button
          v-for="tag in tags.slice(0, 10)"
          :key="tag.id"
          :class="{ active: selectedTagId === tag.id }"
          class="photo-tag-row"
          type="button"
          @click="selectTag(tag.id)"
        >
          <span class="tag-dot" :style="{ backgroundColor: tag.color || '#8b7cf6' }"></span>
          <span>{{ tag.name }}</span>
          <em>{{ tag.count || 0 }}</em>
        </button>
      </section>
    </aside>

    <section class="photo-center">
      <header class="photo-topbar">
        <div class="photo-search">
          <span>⌕</span>
          <input
            v-model.trim="keyword"
            placeholder="搜索照片、标签、描述..."
            @keyup.enter="searchPhotos"
          >
        </div>
        <div class="photo-top-actions">
          <a href="/admin">上传照片</a>
          <a href="/">⌂</a>
        </div>
      </header>

      <section class="photo-library">
        <div class="photo-library-head">
          <div>
            <h1>照片库</h1>
            <p>共 {{ stats.photos || pageData.total || 0 }} 张照片</p>
          </div>
          <div class="photo-filters">
            <select v-model="selectedAlbumId" @change="reloadFirstPage">
              <option value="">全部相册</option>
              <option v-for="album in albums" :key="album.id" :value="album.id">
                {{ album.name }}
              </option>
            </select>
            <select v-model="sortText">
              <option>排序: 最新上传</option>
            </select>
            <button :class="{ active: gridMode === 'grid' }" type="button" @click="gridMode = 'grid'">▦</button>
            <button :class="{ active: gridMode === 'compact' }" type="button" @click="gridMode = 'compact'">☷</button>
          </div>
        </div>

        <div :class="['photo-grid', gridMode]">
          <article
            v-for="item in pageData.records"
            :key="item.photo.id"
            class="photo-card"
            role="button"
            tabindex="0"
            @click="openPreview(item)"
            @keyup.enter="openPreview(item)"
          >
            <img :src="item.photo.thumbnailUrl || item.photo.imageUrl" :alt="item.photo.title || item.photo.originalName">
            <span class="photo-format">{{ item.photo.fileType || fileType(item.photo.originalName) }}</span>
            <span :class="['photo-star', { active: item.photo.isFavorite === 1 }]">☆</span>
            <div class="photo-card-info">
              <strong>{{ item.photo.title || item.photo.originalName || '未命名照片' }}</strong>
              <small>{{ item.albumName || '默认相册' }}</small>
            </div>
          </article>
        </div>

        <div v-if="!loading && pageData.records.length === 0" class="photo-empty">
          暂无照片
        </div>

        <footer class="photo-pager">
          <button type="button" :disabled="page <= 1" @click="changePage(page - 1)">‹</button>
          <span>{{ page }}</span>
          <button type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">›</button>
          <select v-model.number="size" @change="reloadFirstPage">
            <option :value="12">12 条/页</option>
            <option :value="20">20 条/页</option>
            <option :value="30">30 条/页</option>
          </select>
        </footer>
      </section>
    </section>

    <aside class="photo-right">
      <section class="photo-widget">
        <h2>标签云</h2>
        <div class="photo-cloud">
          <button
            v-for="tag in tags.slice(0, 12)"
            :key="tag.id"
            type="button"
            @click="selectTag(tag.id)"
          >
            {{ tag.name }} <span>{{ tag.count || 0 }}</span>
          </button>
        </div>
      </section>

      <section class="photo-widget">
        <h2>时间线</h2>
        <button v-for="item in timeline" :key="item.year" class="photo-line" type="button">
          <span>{{ item.year }}年</span>
          <em>{{ item.count }}</em>
        </button>
      </section>

      <section class="photo-widget">
        <h2>统计信息</h2>
        <p><span>▧ 照片总数</span><strong>{{ stats.photos || 0 }}</strong></p>
        <p><span>▣ 相册数量</span><strong>{{ stats.albums || 0 }}</strong></p>
        <p><span>◇ 标签数量</span><strong>{{ stats.tags || 0 }}</strong></p>
        <p><span>☆ 收藏数量</span><strong>{{ stats.favorites || 0 }}</strong></p>
      </section>
    </aside>

    <div v-if="previewItem" class="image-preview-overlay" @click="closePreview">
      <section class="image-preview-dialog" @click.stop>
        <button class="image-preview-close" type="button" aria-label="关闭预览" @click="closePreview">×</button>
        <img :src="previewImageUrl(previewItem)" :alt="previewTitle(previewItem)">
        <div class="image-preview-info">
          <strong>{{ previewTitle(previewItem) }}</strong>
          <span>{{ previewItem.albumName || '默认相册' }}</span>
          <div v-if="previewItem.tags && previewItem.tags.length" class="image-preview-tags">
            <em v-for="tag in previewItem.tags" :key="tag.id">{{ tag.name }}</em>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { getPhotoAlbums, getPhotos, getPhotoStats, getPhotoTags, getPhotoTimeline } from '../api/photos';

export default {
  name: 'PhotoGallery',
  data() {
    return {
      albums: [],
      tags: [],
      timeline: [],
      stats: {},
      pageData: { total: 0, records: [] },
      page: 1,
      size: 12,
      keyword: '',
      selectedAlbumId: '',
      selectedTagId: '',
      favorite: '',
      sortText: '排序: 最新上传',
      gridMode: 'grid',
      loading: false,
      previewItem: null
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.pageData.total || 0) / this.size));
    }
  },
  created() {
    this.loadMeta();
    this.loadPhotos();
  },
  mounted() {
    window.addEventListener('keydown', this.handlePreviewKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handlePreviewKeydown);
  },
  methods: {
    loadMeta() {
      Promise.all([
        getPhotoAlbums(),
        getPhotoTags(),
        getPhotoTimeline(),
        getPhotoStats()
      ]).then(([albums, tags, timeline, stats]) => {
        this.albums = albums || [];
        this.tags = tags || [];
        this.timeline = timeline || [];
        this.stats = stats || {};
      }).catch(() => {
        this.albums = [];
        this.tags = [];
        this.timeline = [];
        this.stats = {};
      });
    },
    loadPhotos() {
      this.loading = true;
      getPhotos({
        page: this.page,
        size: this.size,
        albumId: this.selectedAlbumId || undefined,
        tagId: this.selectedTagId || undefined,
        favorite: this.favorite || undefined,
        keyword: this.keyword || undefined
      }).then(data => {
        this.pageData = {
          ...data,
          records: data.records || []
        };
      }).catch(() => {
        this.pageData = { total: 0, records: [] };
      }).finally(() => {
        this.loading = false;
      });
    },
    reloadFirstPage() {
      this.page = 1;
      this.loadPhotos();
    },
    changePage(page) {
      this.page = page;
      this.loadPhotos();
    },
    searchPhotos() {
      this.reloadFirstPage();
    },
    selectTag(tagId) {
      this.selectedTagId = this.selectedTagId === tagId ? '' : tagId;
      this.reloadFirstPage();
    },
    clearFilters() {
      this.keyword = '';
      this.selectedAlbumId = '';
      this.selectedTagId = '';
      this.favorite = '';
      this.reloadFirstPage();
    },
    showFavorites() {
      this.favorite = 1;
      this.reloadFirstPage();
    },
    openPreview(item) {
      this.previewItem = item;
    },
    closePreview() {
      this.previewItem = null;
    },
    handlePreviewKeydown(event) {
      if (event.key === 'Escape' && this.previewItem) {
        this.closePreview();
      }
    },
    previewImageUrl(item) {
      return item && item.photo ? (item.photo.imageUrl || item.photo.thumbnailUrl) : '';
    },
    previewTitle(item) {
      if (!item || !item.photo) {
        return '照片预览';
      }
      return item.photo.title || item.photo.originalName || '未命名照片';
    },
    fileType(name) {
      if (!name || name.indexOf('.') === -1) {
        return 'IMG';
      }
      return name.split('.').pop().toUpperCase();
    }
  }
};
</script>
